// Инфографика: нормализация дескрипторов шаблонов из /social_pictures/get_all
// и подготовка HTML-превью. Перенос вкладки "Публикации в соц сети" из приложения
// (ingame_admin/src/layouts/CreateSocialPost.js).

import { BASE_URL, SOCIAL_PALETTES, SOCIAL_CHANGE_COLORS } from '../constants/config';

// Бэкенд вшивает в HTML превью абсолютные ссылки на картинки по http.
// Приложение подменяет их на https://37.46.130.153:3001 и глотает ошибку
// сертификата через ignoreSslError в WebView. В браузере так нельзя:
// у этого адреса невалидный сертификат, и картинки просто не загрузятся.
// Поэтому переписываем на основной хост API, который отдаёт файлы по https.
const INSECURE_HOSTS = [
  'http://37.46.130.153:3000',
  'https://37.46.130.153:3001',
  'http://api.ingame-server.ru',
];

export const secureAssetUrls = (html) => {
  if (typeof html !== 'string') return html;
  let result = html;
  INSECURE_HOSTS.forEach(host => {
    result = result.split(host).join(BASE_URL.url);
  });
  return result;
};

export const resolveImage = (image) => {
  if (!image) return null;
  if (image.startsWith('http') || image.startsWith('data:')) return image;
  return BASE_URL.url + image;
};

// Шаблон доступен админу, если у него не задан список клиентов
// либо client_id админа в этом списке (та же проверка, что в приложении,
// ManageSocialPosts.renderPost)
export const isPatternAvailable = (pattern, clientId) => {
  if (!pattern.client_ids || !pattern.client_ids.length) return true;
  if (!clientId) return false;
  return pattern.client_ids.some(id => Number(id) === Number(clientId));
};

// Дескриптор шаблона: сервер описывает, какие контролы показать
// (available_settings) и без чего картинку не отдать (required_settings)
export const normalizePattern = (pattern) => ({
  ...pattern,
  id: Number(pattern.id),
  title: pattern.title || 'Без названия',
  image: pattern.image || '',
  resizeMode: pattern.resizeMode || 'cover',
  available_settings: Array.isArray(pattern.available_settings)
    ? pattern.available_settings : [],
  required_settings: Array.isArray(pattern.required_settings)
    ? pattern.required_settings : [],
  client_ids: Array.isArray(pattern.client_ids) ? pattern.client_ids : null,
});

// Пропорции превью берём из размера, в котором шаблон отдаётся на сохранение
export const previewAspectRatio = (pattern) => {
  const w = Number(pattern && pattern.cut_size_width);
  const h = Number(pattern && pattern.cut_size_height);
  if (w > 0 && h > 0) return w / h;
  return 16 / 9;
};

// Файл для скачивания: "Турнирная таблица" -> turnirnaya-tablica-2026-09-09.png
const TRANSLIT = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

export const buildFileName = (title) => {
  const slug = (title || 'infographic')
    .toLowerCase()
    .split('')
    .map(char => (TRANSLIT[char] !== undefined ? TRANSLIT[char] : char))
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'infographic';
  const date = new Date().toISOString().slice(0, 10);
  return `${slug}-${date}.png`;
};

// Сохранение base64 в файл: <a download> с blob, чтобы большие картинки
// не уезжали в адресную строку
export const downloadBase64Png = (base64, fileName) => {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }
  downloadBlob(new Blob([bytes], { type: 'image/png' }), fileName);
};

export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ---------- Пакетная выгрузка ----------

// Шаблоны, куда админ вставляет своё фото, пачкой не выгружаются: подобрать
// фон к каждой картинке можно только руками.
//
// Исключение — топ-5. Там фон тоже выбирается, но по умолчанию сервер
// подставляет фото игроков из базы, и такая картинка уже готова к публикации.
const CUSTOM_BG_SETTINGS = [
  'renderCustomBackground',
  'renderCustomBackgroundTwo',
  'renderCustomBackgroundThree',
  'renderCustomBackgroundFour',
  'renderCustomBackgroundFive',
];

const hasCustomBackground = (pattern) => (
  (pattern.available_settings || []).some(name => CUSTOM_BG_SETTINGS.indexOf(name) !== -1)
);

// Топ-5 узнаём по нескольким признакам сразу: тип 4 из каталога, наличие
// выбора статистики или само название. Один признак ненадёжен — у части
// шаблонов дескриптор описан иначе, а терять топ-5 из выгрузки нельзя
export const patternIsTopFive = (pattern) => (
  Number(pattern.type) === 4
  || patternHasEventTypes(pattern)
  || /топ\s*-?\s*5/i.test(pattern.title || '')
);

export const patternNeedsCustomImage = (pattern) => (
  hasCustomBackground(pattern) && !patternIsTopFive(pattern)
);

// Шаблоны, где состав собирает сам админ: сборная тура, MVP, тренеры,
// сетка плей-офф. Их пачкой не собрать.
//
// Сюда НЕ входит renderPlayerTeamChange: он только уточняет, чей логотип
// показать у игрока из нескольких команд, и без него сервер берёт команду
// по умолчанию. Из-за него топ-5 ошибочно выпадал из выгрузки.
// renderSchemeChoose тоже не нужен — он всегда идёт в паре со сборной тура,
// которую ловит renderTeamChoose.
const MANUAL_PICK_SETTINGS = [
  'renderPlayerChoose',
  'renderTeamChoose',
  'renderPlayoffChoose',
  'renderMainTeamChoose',
  'renderSubTeamChoose',
  'renderCoachesChoose',
];

export const patternNeedsManualPick = (pattern) => (
  (pattern.available_settings || []).some(name => MANUAL_PICK_SETTINGS.indexOf(name) !== -1)
);

// Топ-5 рисуется в нескольких вариантах — бомбардиры, ассистенты, гол+пас
// и так далее. В пачку идут все выбранные типы
export const patternHasEventTypes = (pattern) => (
  (pattern.available_settings || []).indexOf('renderEventTypeChoose') !== -1
);

// Таблицы по группам: без группы сервер картинку не собирает,
// поэтому такой шаблон разворачиваем по всем группам сезона
export const patternHasGroups = (pattern) => (
  (pattern.available_settings || []).indexOf('renderLeagueChoose') !== -1
  || (pattern.available_settings || []).indexOf('renderLeagueChooseMultiple') !== -1
);

// Шаблон рисуется на конкретный матч (афиша, результат) — такие в пачке
// повторяются по разу на каждый матч. Остальные (таблица, топ-5, сборная)
// рисуются на тур целиком, по одной картинке
export const patternIsPerMatch = (pattern) => (
  (pattern.required_settings || []).indexOf('activeMatch') !== -1
  || (pattern.available_settings || []).indexOf('renderMatchChoose') !== -1
);

// Имя файла внутри архива: кириллицу оставляем, режем только то,
// что запрещено в путях
export const safeFileName = (value, fallback) => {
  const cleaned = (value || '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || fallback || 'file';
};


// Какая палитра доступна шаблону. Перенос getAvailbleEvoColors из приложения
// (ingame_admin/src/layouts/CreateSocialPost.js) — привязка к id шаблона там
// захардкожена диапазонами, повторяем один в один, чтобы цвета не разъехались.
export const getSocialPalette = (patternId) => {
  const id = Number(patternId);
  if (id <= 15) return SOCIAL_PALETTES.evo;
  if (id <= 47) return SOCIAL_PALETTES.evo_new;
  if (id < 60) return SOCIAL_PALETTES.evo_3;
  if (id < 71) return SOCIAL_PALETTES.jfl;
  if (id === 100) return SOCIAL_PALETTES.lfk;
  if ([101, 102, 104, 105, 106, 107, 108].indexOf(id) !== -1) return SOCIAL_PALETTES.evo_v2_3;
  if (id < 109) return SOCIAL_PALETTES.evo_v2;
  if (id < 117) return SOCIAL_PALETTES.lfk;
  if (id === 117 || id === 118) return SOCIAL_PALETTES.evo_v2_2;
  if (id === 119) return SOCIAL_PALETTES.evo_v2_3;
  if (id === 122) return SOCIAL_PALETTES.lfk;
  if (id === 123) return SOCIAL_PALETTES.aksai;
  return SOCIAL_PALETTES.lfk;
};

// Шаблоны, которые в приложении предлагают заодно опубликовать новость
// (CreateSocialPost.prepareToShare)
export const NEWS_PATTERN_IDS = [1, 4, 6, 9];

export const patternPostsNews = (patternId) => (
  NEWS_PATTERN_IDS.indexOf(Number(patternId)) !== -1
);


// Сколько цветов настраивается у шаблона и какие они по умолчанию.
// Если шаблона нет в справочнике — считаем, что настраиваются два цвета
export const getChangeableColors = (patternId) => (
  SOCIAL_CHANGE_COLORS[Number(patternId)] || ['#ffffff', '#ffffff']
);

// Названия слотов цвета — в приложении их нет, но на широком экране
// подписи помогают понять, что именно красишь
export const COLOR_SLOT_LABELS = ['Акцент', 'Основной', 'Дополнительный'];


// Текст новости, которая публикуется вместе с картинкой.
// Перенос CreateSocialPost.doublePicture из приложения. Разметка ссылок
// {///href///}подпись{//href//}Player-ingame-<id>{///href///} — формат ленты,
// приложение по нему подставляет переход на игрока или лигу.
const HREF_OPEN = '{///href///}';
const HREF_MID = '{//href//}';

// Перевод строки в тексте новости
const NL = String.fromCharCode(10);

const link = (label, target) => `${HREF_OPEN}${label}${HREF_MID}${target}${HREF_OPEN}`;

const withStage = (stage, suffix) => (
  stage && stage.name ? `${stage.name}. ${suffix}` : suffix
);

// Заголовок топ-5 зависит от выбранного типа статистики
const TOP_TITLES = {
  1: 'Топ 5 бомбардиров',
  2: 'Топ 5 ассистентов',
  3: 'Топ 5 жк',
  4: 'Топ 5 кк',
};

export const buildNewsData = (patternId, comment, context) => {
  const id = Number(patternId);
  const { player, players, stage, league, eventValue } = context;
  const text = comment || '';

  if (id === 1) {
    // Лучший игрок тура. В приложении ссылка добавляется даже без выбранного
    // игрока и получается пустой — здесь строку добавляем только когда есть кого
    if (!player || !player.family) {
      return { text, title: withStage(stage, 'Лучший игрок тура') };
    }
    const name = `${player.family} ${player.name || ''}`.trim();
    return {
      text: `${text}${NL}Лучший игрок тура - ${link(name, `Player-ingame-${player.id}`)}`,
      title: withStage(stage, 'Лучший игрок тура'),
    };
  }

  if (id === 6) {
    const target = league ? `League-ingame-${league.league_id}` : '';
    return {
      text: `${text}${NL}Перейти к ${link('таблице', target)}`,
      title: withStage(stage, 'Турнирная таблица'),
    };
  }

  if (id === 9) {
    const target = league ? `League-ingame-${league.league_id}` : '';
    return {
      text: `${text}${NL}Перейти к ${link('статистике лиги', target)}`,
      title: TOP_TITLES[Number(eventValue)] || TOP_TITLES[1],
    };
  }

  if (id === 4) {
    const lines = (Array.isArray(players) ? players : [])
      .filter(one => one.player_id && one.family)
      .map(one => {
        const name = `${one.family} ${one.name || ''}`.trim();
        const position = (one.position_name || '').substring(0, 3);
        return `${position}: ${link(name, `Player-ingame-${one.player_id}`)}`;
      });
    const list = lines.length ? `${lines.join(NL)}${NL}` : '';
    return {
      text: `${text}${NL}Игроки:${NL}${list}`,
      title: withStage(stage, 'Сборная тура'),
    };
  }

  return { text, title: '' };
};
